// ErrorBoundary.tsx
// ----------------------------------------------------------------
import React from 'react';
import { Button } from '@/components/ui/button';
import type { FallbackProps } from 'react-error-boundary';
// ----------------------------------------------------------------

type ErrorFallbackProps = FallbackProps & {
  moveToRoot?: () => void | Promise<void>;
};

/**
 * ErrorFallback is a functional component that serves as a fallback UI
 * for error boundaries in the application. It displays an error message
 * and provides a button to reset the error boundary, allowing users to
 * attempt to recover from the error.
 */
function ErrorFallback({ error, resetErrorBoundary, moveToRoot }: ErrorFallbackProps) {
  // Format error message to be more user-friendly
  const getErrorMessage = (error: Error) => {
    const isNotFoundError = error?.message?.toLowerCase()?.includes('not found') || error?.message?.toLowerCase()?.includes('404');
    if (isNotFoundError) {
      return 'Error: The page you are looking for does not exist. Please check the URL and try again.';
    }
    const message = error?.message?.toLowerCase() || '';

    if (message.includes('network') || message.includes('failed to fetch')) {
      return 'Error: Unable to connect to the server. Please check your internet connection.';
    }
    if (message.includes('timeout')) {
      return 'Error: The request took too long to complete. Please try again.';
    }
    if (message.includes('unauthorized') || message.includes('401')) {
      return 'Error: Your session has expired. Please log in again.';
    }
    return `Error: Something unexpected happened. Please try again or refresh the page. ${error?.message}`;
  };

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-6 p-4 text-center">
      <h1 className="text-5xl font-bold">Oops! Something went wrong</h1>

      <div className="max-w-md space-y-4">
        <p className="text-error-E-dark text-lg">{getErrorMessage(error as Error)}</p>

        <p className="text-sm text-gray-600">
          If this problem persists, please contact support or try refreshing the page.
        </p>
      </div>

      <div className="flex gap-4">
        <Button
          onClick={resetErrorBoundary}
          role="button"
          aria-label="Try again"
          className="bg-primary hover:bg-primary/90"
        >
          Try Again
        </Button>

        <Button
          onClick={() => window.location.reload()}
          variant="outline"
          role="button"
          aria-label="Refresh page"
        >
          Refresh Page
        </Button>
        <Button
          onClick={() => {
            void moveToRoot?.();
          }}
          variant="outline"
          role="button"
          aria-label="Go to Root"
        >
          Go to Root
        </Button>
      </div>
    </div>
  );
}

export default React.memo(ErrorFallback);
